import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "_shared/_stores/root-reducer";

export type ConnectionHealthStatus = "connecting" | "connected" | "reconnecting" | "disconnected" | "stopped";

export type ConnectionHealthRecord = {
  key: string;
  label: string;
  status: ConnectionHealthStatus;
  lastChangedAt: string;
  lastError: string | null;
};

type ConnectionHealthState = {
  byKey: Record<string, ConnectionHealthRecord>;
};

export type ConnectionHealthRegistration = {
  key: string;
  label: string;
};

export type ConnectionHealthTransition = ConnectionHealthRegistration & {
  status: ConnectionHealthStatus;
  lastError?: string | null;
};

const initialState: ConnectionHealthState = {
  byKey: {},
};

const connectionHealthSlice = createSlice({
  name: "connectionHealth",
  initialState,
  reducers: {
    registerConnection: (state, action: PayloadAction<ConnectionHealthRegistration>) => {
      const { key, label } = action.payload;
      const existing = state.byKey[key];

      state.byKey[key] = {
        key,
        label,
        status: existing?.status ?? "stopped",
        lastChangedAt: existing?.lastChangedAt ?? new Date().toISOString(),
        lastError: existing?.lastError ?? null,
      };
    },
    updateConnectionStatus: (state, action: PayloadAction<ConnectionHealthTransition>) => {
      const { key, label, status, lastError } = action.payload;

      state.byKey[key] = {
        key,
        label,
        status,
        lastChangedAt: new Date().toISOString(),
        lastError: lastError ?? null,
      };
    },
    clearConnectionHealth: () => initialState,
  },
});

const connectionHealthState = (state: RootState) => state.connectionHealth;

export const getConnectionHealthByKey = (key: string) =>
  createSelector(connectionHealthState, (state) => state.byKey[key]);

export const getAllConnectionHealth = createSelector(connectionHealthState, (state) => Object.values(state.byKey));

export const getUnhealthyConnections = createSelector(getAllConnectionHealth, (connections) =>
  connections.filter(
    (connection) =>
      connection.status === "stopped" ||
      connection.status === "reconnecting" ||
      connection.status === "disconnected",
  )
);

export const getOverallConnectionHealth = createSelector(getAllConnectionHealth, (connections) => {
  if (connections.length === 0) return "healthy" as const;
  if (connections.some((connection) => connection.status === "disconnected")) return "disconnected" as const;
  if (connections.some((connection) => connection.status === "stopped")) return "stopped" as const;
  if (connections.some((connection) => connection.status === "reconnecting")) return "reconnecting" as const;
  if (connections.some((connection) => connection.status === "connecting")) return "connecting" as const;
  return "healthy" as const;
});

export const { clearConnectionHealth, registerConnection, updateConnectionStatus } = connectionHealthSlice.actions;
export default connectionHealthSlice.reducer;